import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { http, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ImportCsv } from "@/components/ImportCsv";
import { Plus, Pencil, Trash, FloppyDisk, X, Storefront, MapPin, Wrench } from "@phosphor-icons/react";

const blank = { name: "", brand_id: "", company_id: "", address: "", district: "", area: "" };
const STORES_INVALIDATE = ["stores"];

export default function Stores() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: stores = [] } = useQuery({ queryKey: ["stores"], queryFn: () => http.get("/stores").then(r => r.data) });
  const { data: brands = [] } = useQuery({ queryKey: ["brands"], queryFn: () => http.get("/brands").then(r => r.data) });
  const { data: companies = [] } = useQuery({ queryKey: ["companies"], queryFn: () => http.get("/companies").then(r => r.data) });
  const { data: equipment = [] } = useQuery({ queryKey: ["equipment"], queryFn: () => http.get("/equipment").then(r => r.data) });
  const bMap = Object.fromEntries(brands.map(b => [b.id, b.name]));
  const eqCount = (sid) => equipment.filter(e => e.store_id === sid).length;

  const [modal, setModal] = useState(null); // {mode:'create'|'edit', data:obj}
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const canCreate = ["admin", "general_manager"].includes(user?.role);
  const canEdit = canCreate;
  const canDelete = user?.role === "admin";

  const open = (mode, data = blank) => { setForm({ ...blank, ...data }); setErr(""); setModal({ mode, id: data.id }); };
  const close = () => { setModal(null); setForm(blank); setErr(""); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (modal.mode === "create") {
        await http.post("/stores", form);
      } else {
        await http.patch(`/stores/${modal.id}`, form);
      }
      qc.invalidateQueries({ queryKey: ["stores"] });
      close();
    } catch (ex) { setErr(formatErr(ex?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Soft-delete store "${name}"? It will be hidden from lists.`)) return;
    try { await http.delete(`/stores/${id}`); qc.invalidateQueries({ queryKey: ["stores"] }); }
    catch (ex) { alert(formatErr(ex?.response?.data?.detail)); }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline">Estate</div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight mt-1">Stores</h1>
        </div>
        {canCreate && (
          <div className="flex items-center gap-2">
            <ImportCsv resource="stores" invalidateKeys={STORES_INVALIDATE} />
            <button onClick={() => open("create")} data-testid="store-new-button"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-white text-sm font-medium" style={{ background: "#002FA7" }}>
              <Plus size={14} weight="bold" /> Add store
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {stores.map(s => (
          <div key={s.id} data-testid={`store-card-${s.id}`} className="rmms-card rmms-card-hover relative group overflow-hidden">
            <Link to={`/stores/${s.id}`} data-testid={`store-link-${s.id}`} className="block">
              <div className="aspect-[16/9] bg-slate-100 border-b border-slate-200 overflow-hidden">
                <img alt="" src={s.name.startsWith("KFC")
                  ? "https://images.unsplash.com/photo-1626082929543-12d52181b0e4?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
                  : "https://images.unsplash.com/photo-1767974785671-cfc4c8727147?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
                } className="h-full w-full object-cover opacity-95" />
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-1"><Storefront size={14} /><div className="text-xs uppercase tracking-wider text-slate-500">{bMap[s.brand_id] || "—"}</div></div>
                <div className="font-medium">{s.name}</div>
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin size={10} />{[s.district, s.area].filter(Boolean).join(" · ") || s.address || "—"}</div>
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-1.5 text-[12px] text-slate-600">
                  <Wrench size={11} /> <span className="font-mono-data tabular-nums">{eqCount(s.id)}</span> equipment items
                </div>
              </div>
            </Link>
            {(canEdit || canDelete) && (
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {canEdit && <button onClick={(e) => { e.preventDefault(); open("edit", s); }} data-testid={`store-edit-${s.id}`} className="h-7 w-7 bg-white/95 border border-slate-200 rounded-sm flex items-center justify-center hover:bg-slate-50"><Pencil size={12} /></button>}
                {canDelete && <button onClick={(e) => { e.preventDefault(); remove(s.id, s.name); }} data-testid={`store-delete-${s.id}`} className="h-7 w-7 bg-white/95 border border-slate-200 rounded-sm flex items-center justify-center hover:bg-rose-50 hover:text-rose-600"><Trash size={12} /></button>}
              </div>
            )}
          </div>
        ))}
        {stores.length === 0 && <div className="text-slate-500 text-sm">No stores yet.</div>}
      </div>

      {modal && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="store-modal">
          <form onSubmit={submit} className="bg-white border border-slate-200 rounded-md w-full max-w-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="overline">{modal.mode === "create" ? "New" : "Edit"}</div>
                <h2 className="text-xl font-heading font-semibold tracking-tight mt-1">{modal.mode === "create" ? "Add store" : "Edit store"}</h2>
              </div>
              <button type="button" onClick={close} className="h-8 w-8 rounded-sm hover:bg-slate-100 flex items-center justify-center"><X size={16} /></button>
            </div>
            <div>
              <label className="overline mb-1.5 block">Name *</label>
              <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="store-name-input" className="rmms-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline mb-1.5 block">Company *</label>
                <select required value={form.company_id} onChange={e => setForm({ ...form, company_id: e.target.value })} data-testid="store-company-select" className="rmms-input">
                  <option value="">Select…</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="overline mb-1.5 block">Brand *</label>
                <select required value={form.brand_id} onChange={e => setForm({ ...form, brand_id: e.target.value })} data-testid="store-brand-select" className="rmms-input">
                  <option value="">Select…</option>
                  {brands.filter(b => !form.company_id || b.company_id === form.company_id).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="overline mb-1.5 block">Address</label>
              <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} data-testid="store-address-input" className="rmms-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline mb-1.5 block">District</label>
                <input value={form.district} onChange={e => setForm({ ...form, district: e.target.value })} data-testid="store-district-input" className="rmms-input" />
              </div>
              <div>
                <label className="overline mb-1.5 block">Area</label>
                <input value={form.area} onChange={e => setForm({ ...form, area: e.target.value })} data-testid="store-area-input" className="rmms-input" />
              </div>
            </div>
            {err && <div className="text-sm text-rose-600 border-l-2 border-rose-500 pl-3 py-1">{err}</div>}
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <button type="button" onClick={close} className="h-10 px-4 text-sm border border-slate-200 rounded-md">Cancel</button>
              <button type="submit" disabled={busy} data-testid="store-submit-button"
                className="h-10 px-4 rounded-md text-white text-sm inline-flex items-center gap-1.5" style={{ background: "#002FA7" }}>
                <FloppyDisk size={14} weight="bold" /> {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
